import { Suspense, useState } from "react";
import { styled } from "styled-components";
import {
  ISearchPayload,
  Loading,
  chartType,
  ChartSelection as DashboardChartSelection,
  LineChart,
  PieChart,
  RadarChart,
  BarChart,
  CardChart,
} from "@dataone/lib";
import PromptMessages from "../details-table-response/PromptMessages";
import { Button } from "antd";
import domtoimage from "dom-to-image";
import { LoadingOutlined } from "@ant-design/icons";
import { ExportIcon, PointingArrowIcon } from "@dataone/icons";
import { NoDataIcon } from "@dataone/icons";
import DataTable from "../data-table-response/DataTable";

interface ChartSelectionProps {
  stepRef3?: React.MutableRefObject<null>;
  tableExpanded: boolean;
  response: Partial<ISearchPayload>;
  searchApp: string;
  selectedDbId: string | undefined;
  isLoadingSaveSettings?: boolean;
  setResponse: React.Dispatch<React.SetStateAction<Partial<ISearchPayload>>>;
  openModal: () => void;
}

const ChartSelection = ({
  stepRef3,
  tableExpanded,
  response,
  searchApp,
  selectedDbId,
  isLoadingSaveSettings,
  setResponse,
  openModal,
}: ChartSelectionProps) => {
  const [exportImage, setExportImage] = useState(
    <span style={{ color: "#000" }}>
      <ExportIcon />
    </span>
  );
  const handleChartChange = (key: string, value: string) => {
    setResponse((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const chartHeight = () => {
    return (
      <div
        style={{
          minHeight: "332px",
        }}
      >
        <Loading />
      </div>
    );
  };

  const chartHeader = () => {
    return (
      <div
        style={{
          minHeight: "60px",
        }}
      ></div>
    );
  };

  const handleHover = () => {
    setExportImage(
      <span style={{ color: "#fff" }}>
        <ExportIcon />
      </span>
    );
  };

  const handleMouseLeave = () => {
    setExportImage(
      <span style={{ color: "#000" }}>
        <ExportIcon />
      </span>
    );
  };

  const [isExportLoading, setExportLoading] = useState(false);

  const exportChart = async () => {
    if (isExportLoading) return;
    setExportLoading(true);
    const pageElement = (await document.getElementsByClassName(
      "chart-export-container"
    )[0].parentElement) as HTMLElement;
    const dataUrl = await domtoimage.toJpeg(pageElement);
    var link = document.createElement("a");
    link.download = "chart.jpeg";
    link.href = dataUrl;
    link.click();
    setExportLoading(false);
  };

  return (
    <StyledChartSelection $tableExpanded={tableExpanded}>
      <ChartHeader>
        <h5 style={{ padding: "10px" }}>See it for Yourself</h5>
        <ButtonsContainer>
          <SendButton
            onClick={exportChart}
            isExportLoading={isExportLoading}
            className="export"
          >
            <ExportIcon />
            <h5>{isExportLoading ? "Exporting ..." : "Export"}</h5>
          </SendButton>
          <SendButton
            // onClick={() => {
            //   handleSendTableToDashboard();
            // }}
            onClick={openModal}
            className="send"
          >
            {isLoadingSaveSettings ? (
              <span className="icon-loading">
                <LoadingOutlined />
              </span>
            ) : (
              <PointingArrowIcon />
            )}
            <h5>Send to Dashboard</h5>
          </SendButton>
        </ButtonsContainer>
      </ChartHeader>
      <StyledContent >
        <Suspense fallback={<>{chartHeight()}</>}>
          <StyledChartContainer $isExportLoading={isExportLoading} $bgColor={response.backgroundColor || "#fff"}>
            <div className="chart-export-container">
              {response.rows?.length! > 0 &&
                response.columnName &&
                response.chartType === chartType.LINE && (
                  <LineChart
                    isEditable={false}
                    chartId="lineChart"
                    data={response?.data}
                    color={response?.colors}
                    seriesList={response.rows}
                    hasLegend={response.hasLegend}
                    title={response.title}
                    titleBold={response.titleBold}
                    titleColor={response.titleColor}
                    titleUnderline={response.titleUnderline}
                    titleItalic={response.titleItalic}
                    subTitle={response.subTitle}
                    subtitleBold={response.subTitleBold}
                    subtitleColor={response.subTitleColor}
                    subtitleUnderline={response.subTitleUnderline}
                    subtitleItalic={response.subTitleItalic}
                    selectedColumn={
                      response.columnName ? [response.columnName] : []
                    }
                    selectedRows={response.rows}
                    handleTitleChange={(value: string) =>
                      handleChartChange("title", value)
                    }
                    handleSubTitleChange={(value: string) =>
                      handleChartChange("subTitle", value)
                    }
                    lineFillColor={response.lineFillColor}
                    lineChartType={response.lineChartType}
                    target={response.target}
                    targetValue={response.targetValue}
                    underTargetColors={response.underTargetColors}
                    // default chart settings
                    enableFormattedNumbers={response.enableFormattedNumbers}
                    displayChartLabels={response.displayChartLabels}
                  />
                )}
              {response.rows?.length! > 0 &&
                response.columnName &&
                response.chartType === chartType.BAR && (
                  <BarChart
                    isEditable={false}
                    chartId="barChart"
                    data={response?.data}
                    isVertical={response?.isVertical}
                    {...(response.rows?.length === 1 && {
                      seriesList: response.rows,
                    })}
                    color={response?.colors}
                    hasLegend={response.hasLegend}
                    title={response.title}
                    titleBold={response.titleBold}
                    titleColor={response.titleColor}
                    titleUnderline={response.titleUnderline}
                    titleItalic={response.titleItalic}
                    subTitle={response.subTitle}
                    subtitleBold={response.subTitleBold}
                    subtitleColor={response.subTitleColor}
                    subtitleUnderline={response.subTitleUnderline}
                    subtitleItalic={response.subTitleItalic}
                    selectedColumn={
                      response.columnName ? [response.columnName] : []
                    }
                    selectedRows={response.rows}
                    handleTitleChange={(value: string) =>
                      handleChartChange("title", value)
                    }
                    handleSubTitleChange={(value: string) =>
                      handleChartChange("subTitle", value)
                    }
                    stackedBar={response.stackedBar}
                    barRowLine={response.barRowLine}
                    barRowLineColor={response.barRowLineColor}
                    // default chart settings
                    enableFormattedNumbers={response.enableFormattedNumbers}
                    displayChartLabels={response.displayChartLabels}
                  />
                )}
              {response.columnName && response.chartType === chartType.CARD && (
                <CardChart
                  isEditable={false}
                  data={response.data}
                  title={response.title}
                  titleBold={response.titleBold}
                  titleColor={response.titleColor}
                  titleUnderline={response.titleUnderline}
                  titleItalic={response.titleItalic}
                  subTitle={response.subTitle}
                  subtitleBold={response.subTitleBold}
                  subtitleColor={response.subTitleColor}
                  subtitleUnderline={response.subTitleUnderline}
                  subtitleItalic={response.subTitleItalic}
                  selectedColumn={response.columnName}
                  cardIntroValue={response.cardIntroValue}
                  cardOutroValue={response.cardOutroValue}
                  cardColumnLigneIndex={response.cardColumnLigneIndex}
                  // default card settings
                  cardIntroBold={response.cardIntroBold}
                  cardIntroFontSize={response.cardIntroFontSize}
                  cardValueBold={response.cardValueBold}
                  cardValueColor={response.cardValueColor}
                  cardValueFontSize={response.cardValueFontSize}
                  cardImage={response.cardImage}
                  cardValuePosition={response.cardValuePosition}
                  handleChartChange={(key: string, value: string) =>
                    handleChartChange(key, value)
                  }
                />
              )}
              {response.rows?.length === 1 &&
                response.columnName &&
                response.chartType === chartType.PIE && (
                  <PieChart
                    isEditable={false}
                    chartId="pieChart"
                    data={response?.data}
                    color={response?.colors}
                    hasLegend={response.hasLegend}
                    title={response.title}
                    titleBold={response.titleBold}
                    titleColor={response.titleColor}
                    titleUnderline={response.titleUnderline}
                    titleItalic={response.titleItalic}
                    subTitle={response.subTitle}
                    subtitleBold={response.subTitleBold}
                    subtitleColor={response.subTitleColor}
                    subtitleUnderline={response.subTitleUnderline}
                    subtitleItalic={response.subTitleItalic}
                    selectedColumn={
                      response.columnName ? [response.columnName] : []
                    }
                    selectedRows={response.rows}
                    seriesList={response.rows}
                    handleTitleChange={(value: string) =>
                      handleChartChange("title", value)
                    }
                    handleSubTitleChange={(value: string) =>
                      handleChartChange("subTitle", value)
                    }
                    isDonut={response.donut}
                    donutLabel={response.donutLabel}
                  />
                )}
              {response.rows?.length! > 1 &&
                response.columnName &&
                response.chartType === chartType.PIE && (
                  <StyledEmptyChart>
                    <div className="no-data">
                      <NoDataIcon />
                    </div>
                    <div className="message">
                      Data not Suitable for Pie Chart
                    </div>
                    <p>Please select only one of the rows</p>
                  </StyledEmptyChart>
                )}
              {(response.rows?.length === 0 || response.columnName === "") &&
                response.chartType !== chartType.CARD && (
                  <StyledEmptyChart>
                    <div className="no-data">
                      <NoDataIcon />
                    </div>
                    <div className="message">Data not found</div>
                    <p>Please select your column & rows</p>
                  </StyledEmptyChart>
                )}
              {response.rows?.length! > 0 &&
                response.columnName &&
                response.chartType === chartType.RADAR && (
                  <RadarChart
                    isEditable={false}
                    color={response?.colors}
                    chartId="radarChart"
                    data={response?.data}
                    hasLegend={response.hasLegend}
                    title={response.title}
                    titleBold={response.titleBold}
                    titleColor={response.titleColor}
                    titleUnderline={response.titleUnderline}
                    titleItalic={response.titleItalic}
                    subTitle={response.subTitle}
                    subtitleBold={response.subTitleBold}
                    subtitleColor={response.subTitleColor}
                    subtitleUnderline={response.subTitleUnderline}
                    subtitleItalic={response.subTitleItalic}
                    seriesList={response.rows}
                    selectedColumn={
                      response.columnName ? [response.columnName] : []
                    }
                    selectedRows={response.rows}
                    handleTitleChange={(value: string) =>
                      handleChartChange("title", value)
                    }
                    handleSubTitleChange={(value: string) =>
                      handleChartChange("subTitle", value)
                    }
                    lineFillColor={response.lineFillColor}
                    // default chart settings
                    enableFormattedNumbers={response.enableFormattedNumbers}
                  />
                )}
            </div>
          </StyledChartContainer>
        </Suspense>
        <StyledElements>
          <Suspense fallback={<>{chartHeader()}</>}>
            <h5>Choose Your Visuals</h5>
            <HeaderContainer>
              <DashboardChartSelection
                isSelection
                selection={[
                  chartType.LINE,
                  chartType.BAR,
                  chartType.PIE,
                  chartType.RADAR,
                  chartType.CARD,
                ]}
                onSelectedChange={(value: string) =>
                  handleChartChange("chartType", value)
                }
                selectedKey={response.chartType}
                chartResponce={true}
              />
            </HeaderContainer>
            <div className="table">
              <h5>Selected values</h5>
              <DataTable
                searchApp={searchApp}
                setResponse={setResponse}
                response={response}
              />
            </div>
          </Suspense>
        </StyledElements>
      </StyledContent>
      <div ref={stepRef3} className="element">
        <Suspense fallback={<></>}>
          <PromptMessages
            selectedDbId={selectedDbId}
            response={response}
            setResponse={setResponse}
          />
        </Suspense>
      </div>
    </StyledChartSelection>
  );
};

export default ChartSelection;

const StyledChartSelection = styled.div<{ $tableExpanded: boolean }>`
  opacity: 1;
  margin-top: 25px;
  .element {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }
`;

const StyledChartContainer = styled.div<{ $isExportLoading: boolean, $bgColor: string }>`
  .chart-export-container {
    background-color: ${({ $bgColor }) => $bgColor};
    border-radius: 10px;
    height: 100%;
    padding: 6px 0px 0px 0px;

    ${({ $isExportLoading }) =>
    $isExportLoading &&
    `
    .ant-typography-edit {
      visibility: hidden;
    }
    `}
  }
  .chart-container {
    height: 322px !important;
    width: 500px;
    margin-inline: auto;
  }
  display: flex;
  flex-direction: column;
  height: 400px;
  border-radius: 15px;
  border: 3px solid #4974f6;
  width: calc(100% - 330px);
  min-width: 484px;
`;
const ExportButton = styled(Button)`
  width: 15% !important;
  height: 41px;
  border: none;
  display: flex;
  justify-content: center;
  background-color: #eff1fd;
  padding: 11px;
  border-radius: 5px;
  &:not([disabled]):hover {
    background-color: #4974f6;
  }
`;
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 3px 5px 5px 5px;
`;
const StyledEmptyChart = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 25px 50px;
  height: 100%;
  width: 100%;
  .message {
    font-size: 18px;
    font-weight: 700;
  }
  p {
    font-size: 14px;
  }
  .no-data {
    font-size: 110px;
  }
`;

const StyledExport = styled.div`
  display: flex;
  align-items: center;
  font-size: 20px;
`;
const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0px;
  h5 {
    margin: 0;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  .ant-divider-vertical {
    border-inline-start: 2px solid #000 !important;
    margin: 0px 20px;
  }
  .export {
    background: #eff1fd;
    padding: 6px 10px;
    border-radius: 6px;
  }
  .send {
    padding: 4px 10px;
    border: 1.5px solid #4974f6;
    border-radius: 6px;
    margin-left: 18px;
    .icon-loading {
      height: 20px;
      margin-left: -4px;
      margin-right: 4px;
    }
  }
`;

const SendButton = styled.div<{ isExportLoading?: boolean }>`
  display: flex;
  align-items: center;
  h5 {
    color: #4974f6;
    margin: 0;
    padding: 0px !important;
  }
  img {
    &:hover {
      opacity: 0.7;
    }
  }
  cursor: ${({ isExportLoading }) =>
    isExportLoading ? "not-allowed" : "pointer"};
  svg {
    display: flex;
    align-items: center;
    font-size: 20px;
    color: #4974f6;
    width: 19px;
    margin-right: 6px;
  }
`;

const StyledContent = styled.div`
  display: flex;
`;
const StyledElements = styled.div`
  display: flex;
  flex-direction: column;
  background: #eff1fd;
  border-radius: 12px;
  margin-left: 10px;
  padding: 10px 0px;
  h5 {
    // margin-left: 20px !important;
    margin: 5px 0px 10px 20px !important;
    padding: 0px !important;
  }
  .table {
    margin-top: auto;
  }
  max-width: 325px;
  width: 325px;
  overflow-x: auto;
  overflow-y: hidden;
  &::-webkit-scrollbar {
    height: 7px;
  }
  &::-webkit-scrollbar-track {
    background-color: #eff1fd;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #040726;
    border-radius: 20px;
  }
`;
